import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, CalendarDays, Clock, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import en from "@/locales/en/publicTrial.json";
import ar from "@/locales/ar/publicTrial.json";
import fr from "@/locales/fr/publicTrial.json";
import {
  listPublicTrialSlots,
  resolvePublicTrialLink,
  submitPublicTrial,
  PublicTrialError,
  type PublicTrialSlot,
  type PublicTrialConfirmation,
} from "@/services/edgeFunctionWrappers/publicTrialsWrapper";

type Lang = "fr" | "en" | "ar";
type Dict = typeof en;

const DICTS: Record<Lang, Dict> = { en, fr: fr as Dict, ar: ar as Dict };
const LANGS: Lang[] = ["fr", "en", "ar"];
const LANG_LABELS: Record<Lang, string> = { fr: "Français", en: "English", ar: "العربية" };
const LEGACY_KSA = "vb20-ksa";

const GOALS = [
  "fat_loss",
  "fitness",
  "performance",
  "toning",
  "posture",
  "rehab",
  "wellness",
  "muscle",
  "return",
  "other",
] as const;

const SIZES = ["XS", "S", "M", "L", "XL", "XXL"] as const;
const FREQUENCIES = ["1", "2", "3", "4+"] as const;

function get(dict: Dict, path: string): string {
  const value = path.split(".").reduce<unknown>(
    (acc, key) => (acc && typeof acc === "object" ? (acc as Record<string, unknown>)[key] : undefined),
    dict,
  );
  return typeof value === "string" ? value : path;
}

function interpolate(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text;
  return text.replace(/\{\{(\w+)\}\}/g, (_, k: string) => String(vars[k] ?? ""));
}

interface FormState {
  full_name: string;
  phone: string;
  email: string;
  age: string;
  gender: "male" | "female" | "";
  height_cm: string;
  weight_kg: string;
  clothing_size: string;
  main_goal: string;
  main_goal_other: string;
  currently_training: boolean | null;
  training_type: string;
  training_frequency: string;
  last_trained: string;
  weekly_availability: string;
  tried_ems: boolean | null;
  ems_feedback: string;
  has_limitation: boolean | null;
  limitation_details: string;
  motivation: string;
}

const INITIAL: FormState = {
  full_name: "",
  phone: "",
  email: "",
  age: "",
  gender: "",
  height_cm: "",
  weight_kg: "",
  clothing_size: "",
  main_goal: "",
  main_goal_other: "",
  currently_training: null,
  training_type: "",
  training_frequency: "",
  last_trained: "",
  weekly_availability: "",
  tried_ems: null,
  ems_feedback: "",
  has_limitation: null,
  limitation_details: "",
  motivation: "",
};

export default function TrialBookingPage() {
  const { token = "" } = useParams<{ token: string }>();
  const isLegacyKsa = token === LEGACY_KSA;
  const [lang, setLang] = useState<Lang>(isLegacyKsa ? "en" : "fr");
  const dict = DICTS[lang];
  const rtl = lang === "ar";
  const t = useCallback(
    (path: string, vars?: Record<string, string | number>) => interpolate(get(dict, path), vars),
    [dict],
  );

  const [gymName, setGymName] = useState<string | null>(null);
  const [linkState, setLinkState] = useState<"loading" | "valid" | "invalid">("loading");
  const [form, setForm] = useState<FormState>(INITIAL);
  const [slots, setSlots] = useState<PublicTrialSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [slotsError, setSlotsError] = useState(false);
  const [selected, setSelected] = useState<PublicTrialSlot | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<PublicTrialConfirmation | null>(null);

  useEffect(() => {
    let active = true;
    setLinkState("loading");
    resolvePublicTrialLink(token)
      .then((gym) => {
        if (!active) return;
        setGymName(gym.name);
        setLinkState("valid");
      })
      .catch(() => active && setLinkState("invalid"));
    return () => {
      active = false;
    };
  }, [token]);

  const brand = isLegacyKsa ? t("brand") : gymName ?? "";

  useEffect(() => {
    if (!brand) return;
    document.title = `${t("title")} | ${brand}`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", `${t("title")} — ${brand}. ${t("subtitle")}`);
  }, [brand, t]);

  const loadSlots = useCallback(async () => {
    setSlotsLoading(true);
    setSlotsError(false);
    try {
      setSlots(await listPublicTrialSlots(token));
    } catch {
      setSlotsError(true);
    } finally {
      setSlotsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (linkState === "valid") loadSlots();
  }, [loadSlots, linkState]);

  const grouped = useMemo(() => {
    const map = new Map<string, PublicTrialSlot[]>();
    for (const s of slots) {
      const list = map.get(s.class_date) ?? [];
      list.push(s);
      map.set(s.class_date, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [slots]);

  const formatDate = useCallback(
    (iso: string) =>
      new Date(`${iso}T00:00:00`).toLocaleDateString(rtl ? "ar-SA" : "en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
      }),
    [rtl],
  );

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (
      !form.full_name.trim() ||
      !form.phone.trim() ||
      !form.height_cm ||
      !form.weight_kg ||
      !form.clothing_size ||
      !form.main_goal
    ) {
      setFormError(t("errors.requiredFields"));
      return;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setFormError(t("errors.email"));
      return;
    }
    if (!selected) {
      setFormError(t("errors.slot"));
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitPublicTrial(token, {
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        age: form.age ? Number(form.age) : null,
        gender: form.gender || null,
        height_cm: Number(form.height_cm),
        weight_kg: Number(form.weight_kg),
        clothing_size: form.clothing_size,
        main_goal: form.main_goal,
        main_goal_other: form.main_goal_other.trim(),
        currently_training: form.currently_training,
        training_type: form.training_type.trim(),
        training_frequency: form.training_frequency,
        last_trained: form.last_trained.trim(),
        weekly_availability: form.weekly_availability,
        tried_ems: form.tried_ems,
        ems_feedback: form.ems_feedback.trim(),
        has_limitation: form.has_limitation,
        limitation_details: form.limitation_details.trim(),
        motivation: form.motivation.trim(),
        class_id: selected.class_id,
        class_date: selected.class_date,
      });
      setConfirmation(result);
    } catch (err) {
      const code = err instanceof PublicTrialError ? err.code : "SERVER_ERROR";
      if (code === "CLASS_FULL" || code === "SLOT_NOT_FOUND") {
        setFormError(t("errors.full"));
        setSelected(null);
        loadSlots();
      } else if (code === "DUPLICATE") {
        setFormError(t("errors.duplicate"));
      } else if (code === "RATE_LIMITED") {
        setFormError(t("errors.rate"));
      } else {
        setFormError(t("errors.generic"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const LanguageToggle = (
    <div className="flex shrink-0 gap-1" role="group" aria-label="Language">
      {LANGS.map((l) => (
        <Button
          key={l}
          type="button"
          variant={l === lang ? "default" : "outline"}
          size="sm"
          className="px-2"
          onClick={() => setLang(l)}
        >
          {LANG_LABELS[l]}
        </Button>
      ))}
    </div>
  );

  if (linkState !== "valid") {
    return (
      <main dir={rtl ? "rtl" : "ltr"} className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-lg rounded-2xl border bg-card p-8 text-center shadow-sm">
          <div className="mb-4 flex justify-center">{LanguageToggle}</div>
          {linkState === "loading" ? (
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" aria-label={t("loadingGym")} />
          ) : (
            <>
              <h1 className="text-xl font-bold">{t("invalidLink.title")}</h1>
              <p className="mt-3 text-muted-foreground">{t("invalidLink.body")}</p>
            </>
          )}
        </div>
      </main>
    );
  }

  if (confirmation) {
    return (
      <main dir={rtl ? "rtl" : "ltr"} className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-lg rounded-2xl border bg-card p-8 text-center shadow-sm">
          <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
          <h1 className="mt-4 text-2xl font-bold">{t("success.title")}</h1>
          <p className="mt-3 text-muted-foreground">
            {t("success.body", {
              date: formatDate(confirmation.class_date),
              time: (confirmation.time ?? "").slice(0, 5),
            })}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">{t("success.note")}</p>
          <Button
            className="mt-6"
            onClick={() => {
              setConfirmation(null);
              setForm(INITIAL);
              setSelected(null);
              loadSlots();
            }}
          >
            {t("success.again")}
          </Button>
        </div>
      </main>
    );
  }

  const sectionTitle = (index: number, title: string, hint?: string) => (
    <div className="mb-4 flex items-start gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
        {index}
      </span>
      <div>
        <h2 className="text-base font-bold uppercase tracking-wide">{title}</h2>
        {hint ? <p className="text-sm text-muted-foreground">{hint}</p> : null}
      </div>
    </div>
  );

  const yesNo = (value: boolean | null, onChange: (v: boolean) => void) => (
    <div className="flex gap-2">
      {[true, false].map((v) => (
        <Button
          key={String(v)}
          type="button"
          variant={value === v ? "default" : "outline"}
          size="sm"
          onClick={() => onChange(v)}
        >
          {v ? t("fields.yes") : t("fields.no")}
        </Button>
      ))}
    </div>
  );

  const chip = (active: boolean) =>
    cn(
      "rounded-full border px-4 py-2 text-sm transition-colors",
      active ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted",
    );

  return (
    <main dir={rtl ? "rtl" : "ltr"} className="min-h-screen bg-muted/30 px-3 py-6 sm:px-4 sm:py-8">
      <div className="mx-auto max-w-3xl space-y-4 sm:space-y-6">
        <header className="rounded-2xl border bg-card p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <p className="text-[11px] font-semibold uppercase leading-relaxed tracking-widest text-muted-foreground">
              {isLegacyKsa ? t("openDay") : ""}
            </p>
            {LanguageToggle}
          </div>
          <div className="mt-4 text-center">
            <p className="text-2xl font-black tracking-widest sm:text-3xl">{brand}</p>
            {isLegacyKsa && <p className="text-xs tracking-[0.4em] text-muted-foreground">{t("brandCity")}</p>}
            <h1 className="mt-4 text-xl font-bold uppercase tracking-wide sm:text-2xl">{t("title")}</h1>
            <p className="text-sm text-muted-foreground">{t("subtitle")}</p>
            <p className="mt-2 text-xs uppercase tracking-widest text-primary">{t("tagline")}</p>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 1. Personal information */}
          <section className="rounded-2xl border bg-card p-5 shadow-sm">
            {sectionTitle(1, t("sections.personal"), t("sections.personalHint"))}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="full_name">{t("fields.fullName")} *</Label>
                <Input
                  id="full_name"
                  value={form.full_name}
                  maxLength={120}
                  onChange={(e) => set("full_name", e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">{t("fields.mobile")} *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={form.phone}
                  maxLength={30}
                  onChange={(e) => set("phone", e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">
                  {t("fields.email")} <span className="text-muted-foreground">({t("optional")})</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  maxLength={255}
                  onChange={(e) => set("email", e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="age">
                    {t("fields.age")} <span className="text-muted-foreground">({t("optional")})</span>
                  </Label>
                  <Input
                    id="age"
                    type="number"
                    min={10}
                    max={100}
                    value={form.age}
                    onChange={(e) => set("age", e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("fields.gender")}</Label>
                  <div className="flex gap-2">
                    {(["male", "female"] as const).map((g) => (
                      <button
                        key={g}
                        type="button"
                        className={chip(form.gender === g)}
                        onClick={() => set("gender", form.gender === g ? "" : g)}
                      >
                        {t(`fields.${g}`)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="height">{t("fields.height")} *</Label>
                  <Input
                    id="height"
                    type="number"
                    min={100}
                    max={250}
                    value={form.height_cm}
                    onChange={(e) => set("height_cm", e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="weight">{t("fields.weight")} *</Label>
                  <Input
                    id="weight"
                    type="number"
                    min={30}
                    max={300}
                    value={form.weight_kg}
                    onChange={(e) => set("weight_kg", e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>{t("fields.clothingSize")} *</Label>
                <div className="flex flex-wrap gap-2">
                  {SIZES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={chip(form.clothing_size === s)}
                      onClick={() => set("clothing_size", s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* 2. Main goal */}
          <section className="rounded-2xl border bg-card p-5 shadow-sm">
            {sectionTitle(2, t("sections.goal"), t("sections.goalHint"))}
            <div className="flex flex-wrap gap-2">
              {GOALS.map((g) => (
                <button
                  key={g}
                  type="button"
                  className={chip(form.main_goal === g)}
                  onClick={() => set("main_goal", g)}
                >
                  {t(`goals.${g}`)}
                </button>
              ))}
            </div>
            {form.main_goal === "other" ? (
              <Input
                className="mt-3"
                maxLength={200}
                value={form.main_goal_other}
                onChange={(e) => set("main_goal_other", e.target.value)}
                placeholder={t("goals.other")}
              />
            ) : null}
          </section>

          {/* 3. Training history */}
          <section className="rounded-2xl border bg-card p-5 shadow-sm">
            {sectionTitle(3, t("sections.history"))}
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label>{t("fields.currentlyTraining")}</Label>
                {yesNo(form.currently_training, (v) => set("currently_training", v))}
              </div>
              {form.currently_training === true ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="training_type">{t("fields.trainingType")}</Label>
                    <Input
                      id="training_type"
                      maxLength={200}
                      value={form.training_type}
                      onChange={(e) => set("training_type", e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="training_frequency">{t("fields.trainingFrequency")}</Label>
                    <Input
                      id="training_frequency"
                      maxLength={100}
                      value={form.training_frequency}
                      onChange={(e) => set("training_frequency", e.target.value)}
                    />
                  </div>
                </div>
              ) : null}
              {form.currently_training === false ? (
                <div className="space-y-1.5">
                  <Label htmlFor="last_trained">{t("fields.lastTrained")}</Label>
                  <Input
                    id="last_trained"
                    maxLength={200}
                    value={form.last_trained}
                    onChange={(e) => set("last_trained", e.target.value)}
                  />
                </div>
              ) : null}
            </div>
          </section>

          {/* 4. Previous experience */}
          <section className="rounded-2xl border bg-card p-5 shadow-sm">
            {sectionTitle(4, t("sections.experience"))}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Label>{t("fields.triedEms")}</Label>
              {yesNo(form.tried_ems, (v) => set("tried_ems", v))}
            </div>
            {form.tried_ems === true ? (
              <div className="mt-4 space-y-1.5">
                <Label htmlFor="ems_feedback">{t("fields.emsFeedback")}</Label>
                <Textarea
                  id="ems_feedback"
                  maxLength={500}
                  value={form.ems_feedback}
                  onChange={(e) => set("ems_feedback", e.target.value)}
                />
              </div>
            ) : null}
          </section>

          {/* 5. Limitations & safety */}
          <section className="rounded-2xl border bg-card p-5 shadow-sm">
            {sectionTitle(5, t("sections.safety"))}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Label className="max-w-xl leading-relaxed">{t("fields.hasLimitation")}</Label>
              {yesNo(form.has_limitation, (v) => set("has_limitation", v))}
            </div>
            {form.has_limitation === true ? (
              <div className="mt-4 space-y-1.5">
                <Label htmlFor="limitation_details">{t("fields.limitationDetails")}</Label>
                <Textarea
                  id="limitation_details"
                  maxLength={500}
                  value={form.limitation_details}
                  onChange={(e) => set("limitation_details", e.target.value)}
                />
              </div>
            ) : null}
            <p className="mt-4 text-xs text-muted-foreground">{t("safetyNote")}</p>
          </section>

          {/* 6. Motivation */}
          <section className="rounded-2xl border bg-card p-5 shadow-sm">
            {sectionTitle(6, t("sections.motivation"))}
            <Label htmlFor="motivation">{t("fields.motivation")}</Label>
            <Textarea
              id="motivation"
              className="mt-2"
              maxLength={1000}
              value={form.motivation}
              onChange={(e) => set("motivation", e.target.value)}
            />
          </section>

          {/* 7. Availability + slot picker */}
          <section className="rounded-2xl border bg-card p-5 shadow-sm">
            {sectionTitle(7, t("sections.availability"))}
            <Label>{t("fields.weeklyAvailability")}</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {FREQUENCIES.map((f) => (
                <button
                  key={f}
                  type="button"
                  className={chip(form.weekly_availability === f)}
                  onClick={() => set("weekly_availability", f)}
                >
                  {f}
                </button>
              ))}
            </div>

            <h3 className="mt-6 text-sm font-semibold uppercase tracking-wide">{t("sections.slot")} *</h3>
            <div className="mt-3 space-y-4">
              {slotsLoading ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("slots.loading")}
                </p>
              ) : slotsError ? (
                <div className="space-y-2">
                  <p className="text-sm text-destructive">{t("slots.error")}</p>
                  <Button type="button" variant="outline" size="sm" onClick={loadSlots}>
                    {t("slots.retry")}
                  </Button>
                </div>
              ) : grouped.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("slots.empty")}</p>
              ) : (
                grouped.map(([date, daySlots]) => (
                  <div key={date}>
                    <p className="mb-2 flex items-center gap-2 text-sm font-semibold capitalize">
                      <CalendarDays className="h-4 w-4 text-primary" />
                      {formatDate(date)}
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {daySlots.map((s) => {
                        const full = s.available <= 0;
                        const isSelected = selected?.class_id === s.class_id && selected?.class_date === s.class_date;
                        return (
                          <button
                            key={`${s.class_id}-${s.class_date}`}
                            type="button"
                            disabled={full}
                            onClick={() => setSelected(s)}
                            className={cn(
                              "rounded-xl border p-3 text-start transition-colors",
                              full && "cursor-not-allowed opacity-50",
                              isSelected ? "border-primary bg-primary/10" : "hover:bg-muted",
                            )}
                          >
                            <p className="flex items-center gap-2 text-sm font-semibold">
                              <Clock className="h-3.5 w-3.5" />
                              {(s.time || "").slice(0, 5)} · {s.name}
                            </p>
                            <p className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                              <User className="h-3.5 w-3.5" />
                              {t("slots.with", { name: s.instructor || "—" })}
                            </p>
                            <p className={cn("mt-1 text-xs", full ? "text-destructive" : "text-primary")}>
                              {full
                                ? t("slots.full")
                                : t(s.available === 1 ? "slots.spotsLeft" : "slots.spotsLeft_other", {
                                    count: s.available,
                                  })}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          {/* Sticky submit bar: always visible while scrolling the form */}
          <div className="sticky bottom-4 z-20 space-y-2">
            {formError ? (
              <p className="rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                {formError}
              </p>
            ) : null}
            <Button
              type="submit"
              size="lg"
              className="w-full rounded-2xl shadow-lg backdrop-blur"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("submitting")}
                </>
              ) : (
                t("submit")
              )}
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
}
